<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, toRef, watch } from "vue";
import { useRouter } from "vue-router";
import { useQueryClient } from "@tanstack/vue-query";
import { QueryKey } from "@/stores/queries/query-keys";
import { useTranslation } from "i18next-vue";
import { trackTab } from "@/analytics/drawing-analytics";
import {
  createRequested,
  consumeCreateRequest,
  typeFilter,
  viewMode,
  toggleTypeFilter,
  clearTypeFilter,
  observationFramePick,
  consumeObservationFramePick,
  type ObservationsDrawerTab,
  type ObservationsViewMode,
} from "@/stores/ObservationsPanelState";
import type {
  FieldObservation,
  FieldObservationType,
} from "@/types/field-observations.types";
import APIClient from "@/APIClient";
import { DraggerType, Facade } from "@/drawing";
import DraggerMenuState from "@/stores/DraggerMenuState";
import { useGetFieldObservations } from "@/stores/queries/field-observations.query";
import {
  useObservationPlanLinks,
  buildPlanContext,
} from "@/composable/useObservationPlanLinks";
import { provideObservationsList } from "@/composable/useObservationsList";
import { useObservationFramesFeed } from "@/composable/useObservationFrames";
import { useCreatePlanObservation } from "@/composable/useCreatePlanObservation";
import { buildLegendNoteInfo } from "@/composable/useLegendNoteInfo";
import { useDrawingDiagnosticLink } from "@/composable/useDrawingDiagnosticLink";
import {
  buildObservationLetters,
  sortByCreation,
} from "@/utils/observation-plan";
import { useDeviceState } from "@/stores/DeviceState";
import { usePlanAnalysisState } from "@/stores/PlanAnalysisState";
import ConfirmationModal from "@/components/generic/ConfirmationModal.vue";
import DrawerMenuContent from "../DrawerMenuContent.vue";
import ObservationCard from "./ObservationCard.vue";
import ObservationsGroupedList from "./ObservationsGroupedList.vue";
import ObservationsListToolbar from "./ObservationsListToolbar.vue";
import ObservationsDrawerTabs from "./ObservationsDrawerTabs.vue";
import ObservationsEmptyState from "./ObservationsEmptyState.vue";
import ObservationsNotLinked from "./ObservationsNotLinked.vue";
import LinkedDiagnosticBar from "./LinkedDiagnosticBar.vue";
import DrawingLinkDialog from "./DrawingLinkDialog.vue";
import PhotoMarkerPickerPopup from "./PhotoMarkerPickerPopup.vue";
import AnalyzePlanButton from "@/components/canvas/analysis/AnalyzePlanButton.vue";
import DevPurgeButton from "@/components/canvas/analysis/DevPurgeButton.vue";
import DRBtn from "@/components/UI/DRBtn.vue";

/**
 * Onglet « Observations » du volet : les observations réellement créées sur
 * l'ouvrage (à la main, ou validées depuis un groupe d'analyse). Chacune est
 * une carte qui s'ouvre EN PLACE dans la liste (une seule à la fois) ; celles
 * qui ont des éléments sur ce plan y sont entourées d'un cadre bleu portant
 * leur lettre. Création depuis la sélection du plan, un marqueur photo ou
 * une partie de l'arborescence — l'observation est créée, puis sa carte
 * s'ouvre pour la compléter.
 */

const props = defineProps<{
  drawingId: number;
  /** Onglets proposés par le volet (un seul = pas de barre d'onglets). */
  tabs: ObservationsDrawerTab[];
}>();

defineEmits<{
  (e: "close"): void;
}>();

const { t } = useTranslation();
const deviceState = useDeviceState();
const planAnalysis = usePlanAnalysisState();

// ── Plan ↔ ouvrage ──────────────────────────────────────────────────────────
const { drawing, isLoadingDrawing, diagnosticId } = useDrawingDiagnosticLink(
  props.drawingId,
);

onMounted(() => {
  // Résout l'état online (null au démarrage) pour ne pas griser les boutons.
  deviceState.checkIfOnline();
});

const showLinkDialog = ref(false);

function openLinkDialog(): void {
  trackTab("observations", "diagnostic_link_opened");
  showLinkDialog.value = true;
}

// ── Observations ────────────────────────────────────────────────────────────

const {
  data: observations,
  isLoading,
  isFetching,
} = useGetFieldObservations(toRef(diagnosticId));

// Rattachements plan (XData) + sélection courante
const {
  entitiesByObservation,
  selectionHandles,
  selectedObservationIds,
  stampHandles,
  detachHandles,
  clearObservation,
} = useObservationPlanLinks();

// De la première à la dernière créée : la liste se lit dans l'ordre des
// lettres (A en tête), celui des cadres sur le plan.
const sortedObservations = computed(() =>
  sortByCreation(observations.value ?? []),
);

/** Lettre de chaque observation (A = la plus ancienne) — carte et cadre. */
const letters = computed(() =>
  buildObservationLetters(observations.value ?? []),
);

// ── Carte ouverte (une seule à la fois) ─────────────────────────────────────

const openObservationId = ref<number | null>(null);
/** Mode « cliquez sur les éléments à rajouter » de la carte ouverte. */
const addModeActive = ref(false);
/** Cf. `ObservationsListContext.revealToken`. */
const revealToken = ref(0);

// ── Cadres bleus sur le plan ────────────────────────────────────────────────
// Un cadre par observation qui a des entités lisibles sur ce plan, celui de
// la carte ouverte mis en avant ; fournis tant que cet onglet est affiché.
// `zoomOnObservation` cadre le plan sur une observation SANS la sélectionner.
const { zoomOn: zoomOnObservation } = useObservationFramesFeed({
  observations,
  letters,
  entitiesByObservation,
  isCurrent: (observation) => observation.id === openObservationId.value,
});

/**
 * Ouvre la carte d'une observation. `showOnPlan` : cadre le plan sur elle,
 * comme un tap sur la carte d'un groupe d'analyse — pas à la création ni
 * depuis un badge du plan, où l'inspecteur regarde déjà l'endroit.
 */
const openCard = (observation: FieldObservation, showOnPlan: boolean): void => {
  addModeActive.value = false;
  // La cible du mode rattachement de la popup marqueurs change avec la carte.
  if (markerPickerMode.value === "attach") markerPickerOpen.value = false;
  openObservationId.value = observation.id;
  if (showOnPlan) zoomOnObservation(observation);
};

const openObservation = (observation: FieldObservation): void => {
  trackTab("observations", "observation_opened");
  openCard(observation, true);
};

const closeObservation = (): void => {
  addModeActive.value = false;
  openObservationId.value = null;
  // La cible du mode rattachement disparaît avec la carte.
  if (markerPickerMode.value === "attach") markerPickerOpen.value = false;
};

// L'observation ouverte a disparu (supprimée ailleurs) : plus de carte ouverte.
// Jugé sur une liste à jour seulement — pendant une relecture (celle qui suit
// une création, par exemple), l'absence ne prouve rien.
watch([observations, isFetching], ([list, fetching]) => {
  if (
    !fetching &&
    openObservationId.value !== null &&
    list &&
    !list.some((observation) => observation.id === openObservationId.value)
  ) {
    closeObservation();
  }
});

const toggleAddMode = (): void => {
  addModeActive.value = !addModeActive.value;
  if (addModeActive.value) {
    DraggerMenuState.setActiveDragger({ name: DraggerType.Select });
  }
};

// ── Filtre par type + mode de tri ───────────────────────────────────────────

const handleToggleType = (type: FieldObservationType): void => {
  trackTab("observations", "type_filter_used");
  toggleTypeFilter(type);
};

/**
 * La carte ouverte reste dans la liste même si le filtre l'exclut : changer
 * son type en cours d'édition ne la fait pas disparaître sous le doigt.
 */
const filteredObservations = computed(() =>
  typeFilter.value.size === 0
    ? sortedObservations.value
    : sortedObservations.value.filter(
        (observation) =>
          typeFilter.value.has(observation.type) ||
          observation.id === openObservationId.value,
      ),
);

/** Lien plan↔ouvrage et observations chargés : le contenu de la liste est connu. */
const isListReady = computed(() => !isLoadingDrawing.value && !isLoading.value);

/**
 * L'ouvrage n'a encore AUCUNE observation (≠ liste vidée par le filtre, qui
 * ne compte pas tant qu'il n'y a rien à filtrer) : les boutons de création et
 * d'analyse quittent le pied pour se centrer sous le message de liste vide.
 * Faux tant que la liste charge — les boutons ne se déplacent qu'une fois.
 */
const hasNoObservation = computed(
  () => isListReady.value && sortedObservations.value.length === 0,
);

function setViewMode(mode: ObservationsViewMode): void {
  trackTab("observations", "view_mode_changed", { mode });
  viewMode.value = mode;
}

// ── Popup marqueurs photo (persistante, par-dessus le canvas) ───────────────

const markerPickerOpen = ref(false);
const markerPickerMode = ref<"attach" | "browse">("browse");
/** localId de l'observation cible en mode attach (résolu par la carte). */
const markerPickerLocalId = ref<string | undefined>();

const toggleMarkerPickerBrowse = (): void => {
  if (markerPickerOpen.value && markerPickerMode.value === "browse") {
    markerPickerOpen.value = false;
    return;
  }
  // La popup sélectionne les marqueurs qu'on y parcourt : hors mode ajout.
  addModeActive.value = false;
  markerPickerMode.value = "browse";
  markerPickerLocalId.value = undefined;
  markerPickerOpen.value = true;
};

const openMarkerPickerAttach = (observationLocalId: string): void => {
  addModeActive.value = false;
  markerPickerMode.value = "attach";
  markerPickerLocalId.value = observationLocalId;
  markerPickerOpen.value = true;
};

// ── Création ────────────────────────────────────────────────────────────────

const { isCreating, createObservation } = useCreatePlanObservation({
  diagnosticId: toRef(diagnosticId),
  stampHandles,
  onCreated: (observation) => openCard(observation, false),
});

/**
 * Sélection du plan à l'instant de la création — lue sur le moteur, pas sur
 * `selectionHandles` : ce dernier n'est rempli qu'au montage, alors qu'une
 * création demandée depuis le menu de sélection peut démarrer pendant le
 * setup (volet ouvert sur un autre onglet), sélection encore vide.
 */
const currentSelection = (): string[] =>
  Facade.isInitialized
    ? [...Facade.selection.handles]
    : [...selectionHandles.value];

/**
 * Nouvelle observation : la sélection courante du plan lui est rattachée
 * d'office. `partId` = partie imposée (« + » sur une partie de l'arborescence).
 */
const createFromSelection = (partId?: number): void => {
  void createObservation({ handles: currentSelection(), partId });
};

/** Popup marqueurs, mode exploration : une observation à partir d'un marqueur. */
const createFromMarker = (handle: string): void => {
  markerPickerOpen.value = false;
  void createObservation({ handles: [handle] });
};

// Création demandée depuis le menu de sélection (volet déjà ouvert par
// CanvasDrawersMenus) — nécessite un plan lié à un ouvrage.
watch(
  createRequested,
  (requested) => {
    if (requested && diagnosticId.value) {
      createFromSelection();
      consumeCreateRequest();
    }
  },
  { immediate: true },
);
// Le lien plan↔ouvrage peut arriver après le mount (chargement Dexie/serveur).
watch(diagnosticId, (id) => {
  if (id && createRequested.value) {
    createFromSelection();
    consumeCreateRequest();
  }
});

// ── Cartes : contexte partagé (liste par date, par partie, arborescence) ────

provideObservationsList({
  diagnosticId: toRef(diagnosticId),
  openObservationId,
  letters,
  entitiesByObservation,
  selectionHandles,
  selectedObservationIds,
  addModeActive,
  revealToken,
  open: openObservation,
  close: closeObservation,
  stampHandles,
  detachHandles,
  clearObservation,
  toggleAddMode,
  openMarkerPicker: openMarkerPickerAttach,
});

// Badge d'un cadre touché sur le plan : sa carte s'ouvre dans la liste.
watch(observationFramePick, (pick) => {
  if (!pick) return;
  consumeObservationFramePick();
  const [observationLocalId] = pick.ids;
  const observation = observations.value?.find(
    (candidate) => candidate.localId === observationLocalId,
  );
  if (pick.kind !== "badge" || !observation) return;
  // Déjà ouverte : sa carte peut être hors de vue (liste défilée, partie
  // repliée) — on la ramène à l'écran au lieu de ne rien faire.
  if (observation.id === openObservationId.value) {
    revealToken.value++;
    return;
  }
  trackTab("observations", "observation_opened");
  openCard(observation, false);
});

onUnmounted(() => consumeObservationFramePick());

// ── Outil de développement : repartir d'un ouvrage sans observation ─────────

const queryClient = useQueryClient();

/** Supprime toutes les observations de l'ouvrage et libère leurs éléments. */
const purgeObservations = async (): Promise<void> => {
  const all = [...(observations.value ?? [])];
  closeObservation();
  try {
    await Promise.all(
      all.map((observation) => APIClient.deleteFieldObservation(observation.id)),
    );
    for (const observation of all) {
      if (observation.localId) clearObservation(observation.localId);
    }
  } finally {
    await queryClient.invalidateQueries({
      queryKey: [QueryKey.FIELD_OBSERVATIONS],
    });
  }
};

// ── Génération IA des constatations → outil rapport ─────────────────────────

const router = useRouter();
const showGenerateConfirm = ref(false);
const isStartingGeneration = ref(false);

/**
 * Lance la génération (asynchrone côté serveur) avec le contexte plan lu du
 * DWG, puis redirige vers l'onglet Constatations de l'outil rapport où la
 * progression s'affiche.
 */
const startGeneration = async (): Promise<void> => {
  if (!diagnosticId.value) return;
  isStartingGeneration.value = true;
  try {
    const { reportId } = await APIClient.startFindingsGeneration(
      diagnosticId.value,
      buildPlanContext(entitiesByObservation.value, buildLegendNoteInfo()),
    );
    showGenerateConfirm.value = false;
    router.push({
      name: "report-tool",
      params: { reportId },
      query: { tab: "findings" },
    });
  } finally {
    isStartingGeneration.value = false;
  }
};
</script>

<template>
  <DrawerMenuContent
    :label="t('canvas:observations.title')"
    :description="t('canvas:observations.description')"
    :actions="!!diagnosticId"
  >
    <!-- Onglets du volet : seulement s'il y en a plusieurs -->
    <template v-if="tabs.length > 1" #header>
      <ObservationsDrawerTabs :tabs="tabs" :diagnostic-id="diagnosticId" />
    </template>

    <!-- Chargement du lien plan↔ouvrage -->
    <div v-if="isLoadingDrawing" class="text-center py-5">
      <span class="spinner-border spinner-border-sm" />
      <p class="text-grey mt-2 mb-0">
        <small>{{ t("canvas:observations.loading") }}</small>
      </p>
    </div>

    <!-- Plan non lié à un ouvrage -->
    <ObservationsNotLinked
      v-else-if="!diagnosticId"
      :disabled="deviceState.isOnline === false"
      @link="openLinkDialog()"
    />

    <!-- Liste des observations. Les drops hors des tuiles photo sont
         neutralisés : sans ça le navigateur ouvrirait l'image déposée et le
         dessin en cours serait perdu. -->
    <div v-else @dragover.prevent @drop.prevent>
      <ObservationsListToolbar
        :type-filter="typeFilter"
        :view-mode="viewMode"
        :marker-picker-open="markerPickerOpen"
        @toggle-type="handleToggleType"
        @update:view-mode="setViewMode"
        @toggle-marker-picker="toggleMarkerPickerBrowse"
      >
        <template #extra>
          <DevPurgeButton
            :label="t('canvas:observations.dev.purgeObservations')"
            :confirm-message="
              t('canvas:observations.dev.purgeObservationsMessage')
            "
            :disabled="!sortedObservations.length"
            :action="purgeObservations"
          />
        </template>
      </ObservationsListToolbar>

      <div v-if="isLoading" class="text-center py-4">
        <span class="spinner-border spinner-border-sm" />
      </div>

      <ObservationsEmptyState
        v-else-if="!filteredObservations.length"
        :filtered="!hasNoObservation"
        :total="sortedObservations.length"
        :selection-count="selectionHandles.length"
        :can-analyze="!planAnalysis.hasAnalysis"
        :disabled="deviceState.isOnline === false"
        :creating="isCreating"
        @create="createFromSelection()"
        @clear-filter="clearTypeFilter()"
      />

      <ObservationsGroupedList
        v-else-if="viewMode !== 'date'"
        :mode="viewMode === 'tree' ? 'tree' : 'flat'"
        :diagnostic-id="diagnosticId"
        :observations="filteredObservations"
        @create-in-part="createFromSelection($event)"
      />

      <div v-else class="d-flex flex-column gap-2">
        <ObservationCard
          v-for="observation in filteredObservations"
          :key="observation.id"
          :observation="observation"
        />
      </div>
    </div>

    <template #actions>
      <div class="d-flex flex-column gap-2">
        <!-- Sans aucune observation, ces deux boutons sont centrés dans la
             liste (ObservationsEmptyState) : pas de doublon en pied. -->
        <template v-if="!hasNoObservation">
          <DRBtn
            icon="bi-plus-lg"
            :label="
              selectionHandles.length
                ? t('canvas:observations.createFromSelection', {
                    count: selectionHandles.length,
                  })
                : t('canvas:observations.create')
            "
            variant="primary"
            size="sm"
            class="w-100"
            :loading="isCreating"
            :disabled="deviceState.isOnline === false"
            @click="createFromSelection()"
          />
          <!-- Une fois l'analyse faite sur ce poste, la relance est dans
               l'onglet IA. Monté seulement liste chargée : le bouton (et sa
               modale ouverte) ne change pas de place en cours de route. -->
          <AnalyzePlanButton v-if="isListReady && !planAnalysis.hasAnalysis" />
        </template>

        <!-- Pied d'onglet : à quel ouvrage on travaille, et quoi en faire -->
        <LinkedDiagnosticBar
          :name="drawing?.diagnostic?.name"
          :can-generate="!!sortedObservations.length"
          :disabled="deviceState.isOnline === false"
          @change-link="openLinkDialog()"
          @generate="showGenerateConfirm = true"
        />
      </div>
    </template>
  </DrawerMenuContent>

  <DrawingLinkDialog v-model="showLinkDialog" :drawing-id="drawingId" />

  <!-- Confirmation de génération (regénère les constatations du rapport) -->
  <ConfirmationModal
    class="canvas-dialog"
    v-model="showGenerateConfirm"
    :title="t('canvas:observations.generate.confirmTitle')"
    :message="t('canvas:observations.generate.confirmMessage')"
    :confirmLabel="t('canvas:observations.generate.confirm')"
    :loading="isStartingGeneration"
    confirm-variant="ia"
    @confirm="startGeneration"
  />

  <!-- Popup persistante des marqueurs photo du plan -->
  <PhotoMarkerPickerPopup
    v-if="markerPickerOpen && diagnosticId"
    :diagnostic-id="diagnosticId"
    :mode="markerPickerMode"
    :attach-local-id="markerPickerLocalId"
    :entities-by-observation="entitiesByObservation"
    @close="markerPickerOpen = false"
    @stamp="stampHandles"
    @create="createFromMarker"
  />
</template>
